import { estimateExportBytes } from '../lib/exporter';
import { formatBytes, formatClock } from '../lib/format';
import { Panel, Row, Segmented, Slider, Switch } from './ui';
import type { ExportProgress, ExportResult } from '../lib/exporter';
import type { Recording } from '../lib/engine';
import type { Settings } from '../settings';

export type ExportUiState =
  | { status: 'idle' }
  | { status: 'running'; progress: ExportProgress }
  | { status: 'done'; result: ExportResult }
  | { status: 'error'; message: string };

const STAGE_LABEL: Record<ExportProgress['stage'], string> = {
  planning: 'Reading the timeline',
  're-encoding': 'Re-encoding frames',
  muxing: 'Writing the container',
  done: 'Finishing',
};

export function ExportPanel({
  recording,
  trim,
  settings,
  update,
  exportState,
  onExport,
  onCancel,
}: {
  recording: Recording;
  trim: { inUs: number; outUs: number };
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  exportState: ExportUiState;
  onExport: () => void;
  onCancel: () => void;
}) {
  const durationUs = trim.outUs - trim.inUs;
  const sourceBitrate = Math.max(2_000_000, recording.videoBitrate || 12_000_000);
  const bitrateMbps = settings.bitrateMbps ?? Math.round(sourceBitrate / 1_000_000);
  const estimate = estimateExportBytes(
    recording,
    {
      mode: settings.mode,
      bitrate: bitrateMbps * 1_000_000,
      scale: settings.scale,
      includeAudio: settings.includeAudio,
    },
    durationUs,
  );
  const running = exportState.status === 'running';
  const progress = running ? exportState.progress : null;

  return (
    <Panel
      label="Export"
      tone="accent"
      meta={<span className="chip chip--mono">~{formatBytes(estimate)}</span>}
    >
      <Row label="Container" hint={settings.container === 'mp4' ? 'H.264 · AAC · plays anywhere' : 'QuickTime movie · H.264 · AAC'}>
        <Segmented
          name="Container"
          value={settings.container}
          onChange={(value) => update({ container: value })}
          options={[
            { value: 'mp4', label: '.mp4' },
            { value: 'mov', label: '.mov' },
          ]}
        />
      </Row>

      <Row
        label="Cut"
        hint={
          settings.mode === 'fast'
            ? 'No re-encode — instant, pixel-identical'
            : 'Re-encodes the range — cuts land on the exact frame'
        }
      >
        <Segmented
          name="Cut mode"
          value={settings.mode}
          onChange={(value) => update({ mode: value })}
          options={[
            { value: 'fast', label: 'Lossless' },
            { value: 'precise', label: 'Exact' },
          ]}
        />
      </Row>

      {settings.mode === 'precise' ? (
        <>
          <Row label="Scale">
            <Segmented
              name="Scale"
              value={settings.scale}
              onChange={(value) => update({ scale: value })}
              options={[
                { value: 1, label: 'Source' },
                { value: 0.75, label: '75%' },
                { value: 0.5, label: '50%' },
              ]}
            />
          </Row>
          <Row
            label="Bitrate"
            hint={
              settings.bitrateMbps === null
                ? `Following the take at ${(sourceBitrate / 1_000_000).toFixed(1)} Mbps`
                : `Source recorded at ${(sourceBitrate / 1_000_000).toFixed(1)} Mbps`
            }
          >
            <Slider
              label="Bitrate"
              min={2}
              max={40}
              step={1}
              value={bitrateMbps}
              onChange={(value) => update({ bitrateMbps: value })}
              format={(value) => `${value} Mbps`}
            />
          </Row>
        </>
      ) : null}

      {recording.audio ? (
        <Row label="Audio" hint="AAC, kept from the take">
          <Switch
            label="Include audio"
            checked={settings.includeAudio}
            onChange={(value) => update({ includeAudio: value })}
          />
        </Row>
      ) : (
        <Row label="Audio" hint="This take was recorded without audio">
          <span className="chip">silent</span>
        </Row>
      )}

      <div className="export-meta">
        <span className="chip chip--mono">{formatClock(durationUs)}</span>
        <span className="chip chip--mono">
          {recording.width}×{recording.height}
        </span>
        <span className="chip chip--mono">{recording.fps}fps</span>
      </div>

      {progress ? (
        <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress.ratio * 100)}>
          <div className="progress__bar">
            <span className="progress__fill" style={{ width: `${Math.max(2, progress.ratio * 100)}%` }} />
          </div>
          <div className="progress__meta">
            <span>{STAGE_LABEL[progress.stage]}</span>
            <span>{Math.round(progress.ratio * 100)}%</span>
          </div>
        </div>
      ) : null}

      {exportState.status === 'done' ? (
        <p className="note note--ok">
          Saved <strong>{exportState.result.filename}</strong> ·{' '}
          {formatBytes(exportState.result.bytes)} in {(exportState.result.durationMs / 1000).toFixed(1)}s
        </p>
      ) : null}
      {exportState.status === 'error' ? (
        <p className="note note--warn">{exportState.message}</p>
      ) : null}

      <div className="panel__actions">
        {running ? (
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="button" className="button button--primary" onClick={onExport} disabled={running || durationUs <= 0}>
          {running ? 'Exporting…' : `Export .${settings.container}`}
        </button>
      </div>
    </Panel>
  );
}
